import { useEffect, useMemo, useRef, useState } from "react";
import { Consent } from "../../model/Consent";
import { TermsOfUseManagementEditModal } from "./TermsOfUseManagementEditModal";
import companiesQuery from "../../firestore/Companies.query";
import TableList, { GridCol, GridRow } from "../../components/table/TableList";
import Button from "../../components/button/Button";
import { convertDateTimeToString } from "../../utility/ConvertUtily";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import { RadioButton, RadioButtonItem } from "../../components/radiobutton/RadioButton";
import { useInput } from "../../hooks/useInput";

export const TermsOfUseManagementPage = () => {
  const isDisabled = useRef(false);
  const dispatch = useDispatch();

  const appTypeItem: RadioButtonItem[] = [
    {
      id: 'mobile',
      title: 'ジョイン'
    },
    {
      id: 'staff',
      title: 'イベントサポート'
    }
  ];
  const appTypeValue = useInput('mobile');

  const typeItem: RadioButtonItem[] = [
    {
      id: 'termsOfServices',
      title: '利用規約'
    },
    {
      id: 'privacyPolicy',
      title: 'プライバシーポリシー'
    }
  ];
  const typeValue = useInput('termsOfServices');

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isUpdate, setIsUpdate] = useState<boolean>(false);
  const [consent, setConsent] = useState<Consent>();
  const [consentList, setConsentList] = useState<Consent[]>([]);
  const [rows, setRows] = useState<GridRow[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  useMemo(() => {
    const f = async () => {
      const consentList = await companiesQuery.getConsentList();
      if(consentList.success){
        setConsentList(consentList.data);
      }
    };
    f();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[isOpen]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appTypeValue.value, typeValue.value]);

  useEffect(() => {
    const rows = consentList.filter((consent) => consent.appType === appTypeValue.value && consent.type === typeValue.value)
      .map((consent): GridRow  => {
      const updateAt = Number.isNaN(consent.updateAt.getTime())
        ? ''
        : convertDateTimeToString(consent.updateAt);
      return {
        rowData: {
          version: consent.version,
          appType: consent.appType === 'staff' ? 'イベントサポート' : 'ジョイン',
          type: consent.type === 'privacyPolicy' ? 'プライバシーポリシー' : '利用規約',
          title: consent.title,
          updateAt: updateAt,
          edit: (<Button children={'詳細・更新'} onClick={() => {
            setIsUpdate(true);
            setConsent(consent);
            toggle();
          }} />)
        }
      };
    });
    setRows(rows);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[consentList, appTypeValue.value, typeValue.value]);

  const cols: GridCol[] = [
    {
      field: 'version',
      headerName: 'バージョン',
      style: '',
    },
    {
      field: 'appType',
      headerName: '適応範囲',
      style: '',
    },
    {
      field: 'type',
      headerName: '規約形態',
      style: '',
    },
    {
      field: 'title',
      headerName: 'タイトル',
      style: '',
    },
    {
      field: 'updateAt',
      headerName: '作成・更新日時',
      style: '',
    },
    {
      field: 'edit',
      headerName: '更新',
      style: '',
      sort: false
    },
  ];

  const onSubmit = async (data: Consent) => {
    if (!isDisabled.current){
      isDisabled.current = true;
      const result = await companiesQuery.updateConsent(data);
      if(result.success) {
        dispatch(alertOpen(AlertValue('保存しました。', "success")));
        toggle();
      } else {
        dispatch(alertOpen(AlertValue('保存に失敗しました。', "danger")));
      }
      isDisabled.current = false;
    }
  }

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  return (
  <div className="h-full">
    <div className="flex items-center p-6 min-w-[1226px]">
      <h2 className="flex text-xl font-semibold whitespace-nowrap text-gray-900">
        利用規約・個人情報一覧
      </h2>
      <div className={"flex flex-wrap w-full min-w-[466px] pl-14 gap-x-14 gap-y-2"}>
        <div className={"flex"}>
          <RadioButton
            label={"適応範囲"}
            children={undefined}
            name={"appType"}
            radioButtonItem={appTypeItem}
            value={appTypeValue}
            className={"space-x-4 flex items-center space-y-0"}
          />
        </div>
        <div className={"flex"}>
          <RadioButton
            label={"規約形態"}
            children={undefined}
            name={"type"}
            radioButtonItem={typeItem}
            value={typeValue}
            className={"space-x-4 flex items-center space-y-0"}
          />
        </div>
      </div>
      <div className="justify-end min-w-[174px]">
        <Button
          size={"sm"}
          onClick={() => {
            setIsUpdate(false);
            setConsent({
              id: '',
              appType: 'mobile',
              type: 'termsOfServices',
              title: '',
              text: '',
              version: '',
              updateAt: new Date()
            });
            setIsOpen(true);
          }}
        >
          利用規約・個人情報<br/>新規作成
        </Button>
      </div>
    </div>
    {consentList.length > 0 && (
    <div className={"px-6"}>
      <TableList
        gridCol={cols}
        gridRow={rows}
        externalCurrentPage={currentPage}
        setExternalCurrentPage={setCurrentPage}
      />
      <TermsOfUseManagementEditModal
        children={undefined}
        open={isOpen}
        toggle={toggle}
        closeOnClickOutside={false}
        consent={consent}
        onSubmit={onSubmit}
        updateFlg={isUpdate}
      />
    </div>
    )}
  </div>);
}